// The filter strip above a table: one checkbox popover per categorical
// column. Promoted verbatim from admin's `components/table-filters.tsx`
// (zero admin-specific coupling).
//
// Purely a renderer for what `table-filter.ts` computed — which controls to
// show, and what each offers, is decided there so every table using it
// narrows the same way. Drop it into a page's existing toolbar next to the
// search box; it is inline-flex and wraps, so it shares a row rather than
// claiming one.
//
// Renders NOTHING when no control survived, which is the normal state of a
// small table whose every row shares one country: a strip of controls that can
// only say "everything" is noise above ten rows.
import { Button } from "./button";
import type { ColumnFilterControl } from "./table-filter";
import { MultiSelect } from "./multi-select";
import "./table.css";

export interface TableFiltersProps {
  controls: readonly ColumnFilterControl[];
  /** Is anything selected? Drives the escape hatch. */
  active: boolean;
  onClear: () => void;
}

export function TableFilters({ controls, active, onClear }: TableFiltersProps) {
  if (controls.length === 0) return null;
  return (
    <div className="rc-filters">
      {controls.map((control) => (
        <MultiSelect
          key={control.key}
          label={control.label}
          allLabel={control.allLabel}
          options={control.options}
          selected={control.selected}
          onChange={control.onChange}
        />
      ))}
      {/* One button that undoes every narrowing at once. Each popover can clear
          itself, but a reader who has narrowed three dimensions and now sees an
          empty table should not have to find all three. Pages that also own a
          search box fold it into `active`/`onClear`, so the button means the
          same thing everywhere: put the whole list back. */}
      {active ? (
        <Button type="button" size="sm" variant="ghost" tone="neutral" onClick={onClear}>
          Clear filters
        </Button>
      ) : null}
    </div>
  );
}
